import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Video, Copy, Loader2, Clock } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import DailyVideoFrame from "@/components/DailyVideoFrame";
import ConsultRatingDialog from "@/components/ConsultRatingDialog";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function ConsultRoomPage() {
  const { consultId } = useParams();
  const navigate = useNavigate();
  const { user, loading } = useAuth();
  const [joinInfo, setJoinInfo] = useState(null);
  const [error, setError] = useState(null);
  const [showRating, setShowRating] = useState(false);

  const fetchJoinInfo = useCallback(async () => {
    try {
      const { data } = await axios.post(`${API}/consults/${consultId}/join-info`);
      setJoinInfo(data);
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Unable to join this consult.");
    }
  }, [consultId]);

  useEffect(() => {
    if (!loading && !user) navigate("/auth");
    if (user) fetchJoinInfo();
  }, [user, loading, navigate, fetchJoinInfo]);

  const providerLink = joinInfo
    ? `${window.location.origin}/consult/join/${joinInfo.consult.provider_join_code}`
    : "";

  const copyProviderLink = () => {
    navigator.clipboard.writeText(providerLink);
    toast.success("Provider join link copied! Share it by text, phone, or email.");
  };

  return (
    <div className="min-h-screen bg-[#0B1726]">
      <header className="px-4 sm:px-6 py-3 flex items-center justify-between gap-3 max-w-7xl mx-auto">
        <Button data-testid="consult-back-btn" onClick={() => navigate("/portal")} variant="ghost" className="text-white hover:bg-white/10">
          <ArrowLeft className="w-5 h-5 mr-2" /> Portal
        </Button>
        {joinInfo && (
          <div className="flex items-center gap-2 text-white font-body text-sm">
            <Video className="w-4 h-4 text-secondary" />
            Consult with <strong>{joinInfo.consult.provider_name}</strong>
          </div>
        )}
        {joinInfo && (
          <Button data-testid="copy-provider-link-btn" onClick={copyProviderLink} className="bg-secondary text-text-primary hover:bg-secondary/90 rounded-full font-bold font-body text-sm">
            <Copy className="w-4 h-4 mr-2" /> Copy Provider Link
          </Button>
        )}
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 pb-8">
        {error ? (
          <div className="flex flex-col items-center justify-center h-[60vh] gap-4 text-center">
            <Clock className="w-12 h-12 text-secondary" />
            <p data-testid="consult-error-message" className="text-white font-body text-lg max-w-md">{error}</p>
            <Button onClick={() => navigate("/portal")} className="bg-secondary text-text-primary rounded-full font-bold font-body">
              Back to Portal
            </Button>
          </div>
        ) : !joinInfo ? (
          <div className="flex items-center justify-center h-[60vh]">
            <Loader2 className="w-10 h-10 animate-spin text-secondary" />
          </div>
        ) : (
          <div className="space-y-3">
            <DailyVideoFrame
              roomUrl={joinInfo.room_url}
              token={joinInfo.token}
              onLeave={() => setShowRating(true)}
            />
            <p className="text-white/60 font-body text-xs text-center">
              🔒 This is a private, encrypted video room. Only people with a secure invite can join.
            </p>
          </div>
        )}
      </main>

      {joinInfo && (
        <ConsultRatingDialog
          isOpen={showRating}
          providerId={joinInfo.consult.provider_id}
          providerName={joinInfo.consult.provider_name}
          onDone={() => {
            setShowRating(false);
            navigate("/portal");
          }}
        />
      )}
    </div>
  );
}
